/**
 * What a group's Overview (the Insights screen) works out: the member's own spend, the tile
 * numbers, month-by-month spend for a member, and the purchase span. Screens only draw these.
 * Amounts are micro (millionths of a dollar).
 */
import type { Micro } from "./money.ts";
import type { GroupState, IsoDate } from "./types.ts";
import type { Ledger } from "./engine/ledger.ts";
import { daysAgo } from "./overview.ts";

const CENT: Micro = 10_000;

/** Total spend: a member's own part (not counting carried-over balances), or the whole group's. */
export function groupSpendMicro(ledger: Ledger, who?: string): Micro {
  return who
    ? ledger.items.reduce((s, c) => s + (c.category === "Balance" ? 0 : (c.shares[who] ?? 0)), 0)
    : ledger.insights.byStore.reduce((s, b) => s + b.totalMicro, 0);
}

/** A member's own part of each month's spending ("2026-10" to micro), carried-over balances left out. */
export function memberSpendByMonth(ledger: Ledger, who: string): Map<string, Micro> {
  const spent = new Map<string, Micro>();
  for (const c of ledger.items) {
    const v = c.shares[who];
    if (v === undefined || c.category === "Balance") continue;
    const m = c.item.date.slice(0, 7);
    spent.set(m, (spent.get(m) ?? 0) + v);
  }
  return spent;
}

/** Money that came in over the last `days` days in one group: to you from others, or a member's own payments. */
export function groupReceivedMicro(
  state: GroupState,
  today: IsoDate,
  days = 30,
  who?: string,
): Micro {
  const from = daysAgo(today, days);
  const me = state.settings.ownerPersonId;
  let sum = 0;
  for (const id of state.paymentOrder) {
    const p = state.payments[id];
    if (!p || p.at.slice(0, 10) < from) continue;
    if (who ? p.personId === who && p.cents > 0 : p.cents > 0 && p.personId !== me)
      sum += p.cents * CENT;
  }
  return sum;
}

/** What the "owed over time" line ends on: right now's balance (a member's own, or everyone's). */
export function currentOwedMicro(ledger: Ledger, who?: string): Micro {
  return who
    ? (ledger.byPersonId[who]?.balanceMicro ?? 0)
    : ledger.people.reduce(
        (s, p) => s + (p.status === "owes" || p.status === "owed" ? p.balanceMicro : 0),
        0,
      );
}

/** The first and last purchase dates in a group (the range slider's ends); `fallbackLo` if none. */
export function groupPurchaseSpan(
  state: GroupState,
  fallbackLo: IsoDate | null,
): { lo: IsoDate | null; hi: IsoDate | null } {
  let lo = "";
  let hi = "";
  for (const it of Object.values(state.items)) {
    if (!lo || it.date < lo) lo = it.date;
    if (!hi || it.date > hi) hi = it.date;
  }
  return { lo: lo || fallbackLo, hi: hi || null };
}
